import ReactECharts from 'echarts-for-react'
import { useNavigate } from '@tanstack/react-router'

interface PieChartData {
  name: string
  value: number
  uid?: string  // Add UID for navigation
  color?: string
}

interface ClickablePieChartProps {
  data: PieChartData[]
  title: string
  baseUrl: string  // Base URL for navigation (e.g., '/demo/assets' or '/demo/documents')
  queryParam: string  // Query parameter name (e.g., 'asset_type_uid' or 'document_type_uid')
  className?: string
}

const DEFAULT_COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#82CA9D',
  '#FFC658',
  '#FF7C7C',
  '#8DD1E1',
  '#D084D0'
]

export function ClickablePieChart({ data, title, baseUrl, queryParam, className = "" }: ClickablePieChartProps) {
  const navigate = useNavigate()

  // Take only first 5 items and sort by value (descending)
  const sortedData = [...data]
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)

  const chartData = sortedData.map((item, index) => ({
    name: item.name,
    value: item.value,
    uid: item.uid,  // Pass UID through
    itemStyle: {
      color: item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
    }
  }))

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'horizontal',
      bottom: 10,
      textStyle: {
        color: 'var(--foreground)'
      }
    },
    series: [
      {
        name: 'Count',
        type: 'pie',
        radius: ['0%', '60%'],
        center: ['50%', '50%'],
        data: chartData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        label: {
          formatter: '{d}%',
          fontSize: 12,
          color: 'var(--foreground)'
        }
      }
    ]
  }

  const onEvents = {
    'click': (params: any) => {
      const clickedData = chartData[params.dataIndex]
      if (clickedData?.uid) {
        // Navigate to the URL with query parameter
        navigate({
          to: baseUrl,
          search: { [queryParam]: clickedData.uid }
        })
      }
    }
  }

  return (
    <div className={`bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${className}`}>
      <div className="p-6 pb-0">
        <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
      </div>
      <div className="p-6 pt-0">
        <ReactECharts
          option={option}
          style={{ height: '280px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          onEvents={onEvents}
        />
      </div>
    </div>
  )
}